/**
 * Shopify Domain Guard
 * Prevents the React app from running on unauthorized domains
 */

// Get configuration from environment variables
const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN || '';
const CUSTOM_DOMAIN = process.env.REACT_APP_CUSTOM_DOMAIN || '';

// Build allowed domains list from environment variables
const ALLOWED_DOMAINS = [
  SHOPIFY_DOMAIN,                        // Your Shopify store (from .env)
  'admin.shopify.com',                   // Shopify admin
  'localhost',                           // Local development
  '127.0.0.1',                          // Local development
];

// Add custom domain if configured
if (CUSTOM_DOMAIN) {
  ALLOWED_DOMAINS.push(CUSTOM_DOMAIN);
  // Also add without www if it has www
  if (CUSTOM_DOMAIN.startsWith('www.')) {
    ALLOWED_DOMAINS.push(CUSTOM_DOMAIN.replace('www.', ''));
  }
}

// Shopify preview/admin domains (these are safe)
const SHOPIFY_PATTERNS = [
  new RegExp(`^${SHOPIFY_DOMAIN.replace('.', '\\.')}$`),  // Exact match for your store
  /\.shopifypreview\.com$/,
  /\.shopify\.com$/,
  /^localhost$/,
  /^127\.0\.0\.1$/,
];

/**
 * Check if the current domain is allowed
 */
function isAllowedDomain() {
  // Get the parent frame's hostname (where the app is embedded)
  let hostname;
  
  try {
    // Try to get parent hostname (works if same origin)
    hostname = window.parent.location.hostname;
  } catch (e) {
    // Cross-origin - get from referrer
    if (document.referrer) {
      try {
        hostname = new URL(document.referrer).hostname;
      } catch (err) {
        hostname = window.location.hostname;
      }
    } else {
      hostname = window.location.hostname;
    }
  }
  
  console.log('[Shopify Guard] Checking domain:', hostname);
  
  // Check exact matches
  if (ALLOWED_DOMAINS.includes(hostname)) {
    console.log('[Shopify Guard] ✅ Domain allowed (exact match)');
    return true;
  }
  
  // Check pattern matches
  for (const pattern of SHOPIFY_PATTERNS) {
    if (pattern.test(hostname)) {
      console.log('[Shopify Guard] ✅ Domain allowed (pattern match)');
      return true;
    }
  }
  
  console.error('[Shopify Guard] ❌ Domain not allowed:', hostname);
  return false;
}

/**
 * Show unauthorized message
 */
function showUnauthorizedMessage() {
  const shopifyDomain = process.env.REACT_APP_SHOPIFY_DOMAIN || 'your-store.myshopify.com';
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        padding: 40px;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div>
          <h2 style="color: #333; margin-bottom: 16px;">
            🔒 Unauthorized Access
          </h2>
          <p style="color: #666; margin-bottom: 24px;">
            This blend builder can only be accessed through our official store.
          </p>
          <a 
            href="https://${shopifyDomain}/pages/blend-builder" 
            style="
              display: inline-block;
              padding: 12px 24px;
              background: #4A6B4D;
              color: white;
              text-decoration: none;
              border-radius: 4px;
              font-weight: 500;
            "
          >
            Visit Our Store
          </a>
        </div>
      </div>
    `;
  }
}

/**
 * Initialize domain guard
 */
export function initShopifyGuard() {
  // Allow bypassing domain guard for development/testing
  if (process.env.REACT_APP_DISABLE_DOMAIN_GUARD === 'true') {
    console.log('[Shopify Guard] ⚠️ Domain guard disabled for development');
    return true;
  }
  
  if (!isAllowedDomain()) {
    showUnauthorizedMessage();
    return false;
  }
  return true;
}

/**
 * Get allowed domains (for debugging)
 */
export function getAllowedDomains() {
  return ALLOWED_DOMAINS;
}
